import { z } from 'zod';

import { CheckRunSchema, NewsItemSchema, QuarantineSchema, SettingsSchema, TopicSchema } from '../db/schemas.js';
import { ProfileIdsSchema } from '../profiles.js';
import { FocusIdsSchema } from '../topic-focus.js';
import { isHttpUrl } from '../url-safety.js';

// Request schemas (validated server-side).
//
// **A `z.infer` alias is exported when a caller names the type, and not
// otherwise** (NEWS-352). Thirteen aliases were exported that nothing imported;
// the argument for keeping them was that every schema should have a
// symmetrical public type — but the convention was never actually applied, so
// it could not be the reason. Eight schemas had no alias at the time, including
// five `Resp` schemas and one `Req` added the day before beside a sibling that
// did get one. They were incidental, not designed, and a reader could not tell
// which of the two exports beside a schema was load-bearing.
//
// Routes call `parseBody(c, XReqSchema)` and let the type infer at the call
// site, which is why request aliases in particular go unused. Adding one back
// the moment something names it is a one-line change.
//
// `ErrorRespSchema` went with them: removing its alias left it referenced by
// nothing, which is what it had always been — routes answer with an inline
// `c.json({ error: '…' }, 4xx)` and never validate their own error bodies. It
// was a shape nobody was held to.

/**
 * Body of a topic creation.
 *
 * The optional fields exist for discovery (NEWS-126): a topic added from a
 * suggestion arrives with a pre-validated classification (FR-24.13), set **in
 * the same request** rather than PATCHed afterwards because creating a topic
 * fires its first check immediately (FR-1.12) — a follow-up PATCH would land
 * after that check had already run unclassified. (Freeform `guidance` was removed
 * in NM-124; the closed focus vocabulary is the only per-topic steer, set by a
 * later PATCH.)
 */
export const CreateTopicReqSchema = z.object({
  name: z.string().min(1).max(200),
  category: z.string().min(1).optional(),
  subcategory: z.string().min(1).optional(),
});

/**
 * An atomic capacity swap (NM-200): enable a set of subjects and disable another
 * set in one transaction, so the "pick one to disable" dialog can commit "enable
 * X, disable Y" without a transient over-cap the sequential PATCHes would allow.
 * Both default to empty; the server validates the resulting enabled counts stay
 * within the plan's caps and rolls the whole swap back with a 409 if not.
 */
export const CapacitySwapReqSchema = z.object({
  enable: z.array(z.string()).max(500).default([]),
  disable: z.array(z.string()).max(500).default([]),
});
export type CapacitySwapReq = z.infer<typeof CapacitySwapReqSchema>;

/** "Did you mean?" suggestions for a typed topic (NM-148/153) — existing shared subjects to steer toward. */
export const SuggestSimilarRespSchema = z.object({
  suggestions: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        catalog: z.boolean(),
        subscriberCount: z.number().int(),
        mine: z.boolean(),
      }),
    )
    .default([]),
  // The AI meaning-judge's high-confidence "same subject" flag (NM-154, FR-46.7),
  // for the stronger "this looks like the same as X" prompt. Null when the judge
  // found no strong match, was unavailable, or failed open to the lexical rank.
  strongMatch: z.object({ id: z.string(), name: z.string() }).nullable().default(null),
  // Spelling corrections for a name that looks misspelled (NM-179): a corrected
  // real-world entity name + a short descriptor to tell candidates apart. Distinct
  // from `suggestions` (existing subjects to follow) — these are *new* names the
  // user did not type; picking one mints a topic under the corrected spelling.
  // Empty in the common case (the name is spelled fine) or when the classifier
  // failed open.
  corrections: z.array(z.object({ name: z.string(), descriptor: z.string() })).default([]),
});
export type SuggestSimilarResp = z.infer<typeof SuggestSimilarRespSchema>;

/**
 * A shared topic list, on its way back in (FR-30.5–30.9, NEWS-318).
 *
 * The file `GET /api/export-topics.json` writes (FR-30.2) — and, deliberately, a
 * file a person may have edited by hand, since being hand-editable is the point
 * of that format. So this is **lenient about what it ignores and strict about
 * what it accepts**: an `exportedAt` it does not need, or a future field it does
 * not know, must not make a usable list unreadable, but a name that is not a
 * string is a file this cannot honestly import.
 *
 * `guidance`, `category` and `subcategory` are optional so the smallest useful
 * file someone could type — `{"topics":[{"name":"Fusion energy"}]}` — is valid.
 *
 * Bounds match `CreateTopicReqSchema`, because these are the same fields
 * arriving by a different door; a list that could carry a 10,000-character name
 * past the limit the form enforces would be a way around it.
 */
export const ImportTopicsReqSchema = z.object({
  topics: z.array(
    z.object({
      name: z.string().min(1).max(200),
      // Closed focus ids (NM-124), filtered to the allowed vocabulary — an
      // unknown id is dropped, not an error, so an export from a build with a
      // different vocabulary still imports (the extras just default off).
      focus: FocusIdsSchema.optional(),
      // A **legacy** export carried a freeform `guidance` string. Per NM-124 it
      // is honored only if it happens to name an allowed focus id (mapped to
      // `focus` by `importTopics`); any other value is ignored — the default.
      guidance: z.string().optional(),
      category: z.string().min(1).nullish(),
      subcategory: z.string().min(1).nullish(),
    }),
  ),
});

/**
 * An exported story archive, on its way back in (FR-30.10–30.14, NEWS-319).
 *
 * **The file `GET /api/export.json` already writes** (FR-21.4), not a new one.
 * "An export nothing can read" is the whole complaint behind this, and inventing
 * a second shape would leave the first still unreadable.
 *
 * `topic` is the topic's **name**, which is why the export carries a name rather
 * than an id — an id means nothing on another install. `null` is what the export
 * writes for a story whose topic was deleted before the file was made.
 *
 * `saved` is optional and defaults false; `offTopic` is absent by construction,
 * because FR-21.2 keeps flagged stories out of every export. Nothing here can
 * carry one, which is the point (FR-30.13).
 */
export const ImportStoriesReqSchema = z.object({
  stories: z.array(
    z.object({
      topic: z.string().min(1).nullable(),
      title: z.string().min(1),
      summary: z.string().default(''),
      sources: z
        .array(
          z.object({
            title: z.string().default(''),
            url: z.string(),
            outlet: z.string().nullish(),
            publishedAt: z.string().nullish(),
          }),
        )
        .default([])
        // Same http(s) scheme guard as the AI-parse path (NM-122): the import
        // door runs no link verification, so a `javascript:`/`data:` URL here
        // would be stored and rendered into an `<a href>` unchecked.
        .transform((arr) => arr.filter((s) => isHttpUrl(s.url))),
      foundAt: z.string(),
      saved: z.boolean().default(false),
    }),
  ),
});

/** What a story import did (FR-30.7, FR-30.12). */
export const ImportStoriesRespSchema = z.object({
  added: z.number().int(),
  skipped: z.number().int(),
  /** Topics the import had to create, named so the report can say so. */
  topicsCreated: z.array(z.string()),
});
export type ImportStoriesResp = z.infer<typeof ImportStoriesRespSchema>;

/** What an import did, so the UI can say it (FR-30.7). */
export const ImportTopicsRespSchema = z.object({
  added: z.array(z.string()),
  skipped: z.array(z.string()),
});
export type ImportTopicsResp = z.infer<typeof ImportTopicsRespSchema>;

// A topic PATCH may toggle pause, rename, set focus, and/or set the category; at
// least one is required. `category: null` is how the user clears that. (Freeform
// `guidance` was removed in NM-124; `focus` is the steer now. The high-priority
// toggle was removed with the priority concept in NM-150.)
export const UpdateTopicReqSchema = z
  .object({
    paused: z.boolean(),
    /** Rename (NEWS-139). Same bounds as creation — it is the same field. */
    name: z.string().min(1).max(200),
    /**
     * Drop the topic's existing stories (NEWS-139).
     *
     * Only meaningful alongside `name`: it exists so a rename that changes what
     * a topic *means* can discard results that were about the old meaning.
     */
    clearItems: z.boolean(),
    /**
     * The topic's selected focus angles (NM-27), by id. A plain `string[]`, not
     * an enum — same call as `category`. The **ids are validated against the
     * live vocabulary in the route handler**, not here, so an unknown id is a
     * clean 400 that names the offender rather than a schema parse failure; the
     * bound below is only a sanity ceiling. `[]` clears the selection.
     */
    focus: z.array(z.string()).max(50),
    /**
     * Category slug (NEWS-97). A plain nullable string, not an enum — the
     * taxonomy is edited in code (FR-22.3), and an enum here would start
     * rejecting requests the moment someone renamed a slug.
     */
    category: z.string().nullable(),
    subcategory: z.string().nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' })
  // A subcategory without its parent is meaningless — it would render as the
  // bare category anyway, and storing it would look like a classification.
  .refine((v) => !(v.subcategory !== undefined && v.category === undefined), {
    message: 'subcategory requires category',
  })
  // Clearing stories is a consequence of renaming, not an action of its own —
  // there is a delete for that. Accepting it alone would make `PATCH` a way to
  // wipe a topic's history with no rename to justify it.
  .refine((v) => !(v.clearItems === true && v.name === undefined), {
    message: 'clearItems requires name',
  });

export const UpdateSettingsReqSchema = z
  .object({
    // '' clears it. Capped and otherwise unvalidated on purpose (NEWS-393): the
    // value is a phrase for the prompt, not an identifier, so there is nothing
    // to validate it against. `.max` counts UTF-16 code units, which is fine —
    // it is a sanity bound, and 200 is generous even for a place name written
    // in a script where each character carries more.
    location: z.string().max(200),
    // Profile **ids**, deduplicated (NEWS-383). Unknown ids are accepted here
    // and dropped on read rather than rejected — an export written by a build
    // with one extra profile must import, and losing one chip beats losing the
    // import. Same call `categories.ts` makes for an unresolvable slug.
    profiles: ProfileIdsSchema,
    notifyOnNewItems: z.boolean(),
    theme: z.enum(['auto', 'light', 'dark']),
    // '' clears it. Non-empty must be https — a plaintext manifest could be
    // swapped in transit, and it decides what the budget cap acts on.
    priceManifestUrl: z.union([z.literal(''), z.url().startsWith('https://').max(500)]),
    monthlyBudgetUsd: z.number().nonnegative().max(10_000),
    // Story retention is a fixed window now (NM-169), no longer a user setting.
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'at least one setting is required' });

export const CheckReqSchema = z.object({ topicId: z.string().optional() });

/**
 * Body of a sense selection for an ambiguous topic (NM-163, docs/47).
 *
 * The route validates that `sense` is one of the topic's stored `senseOptions`
 * before writing it — the string is trusted no further than the create name is,
 * and only a sense the app itself offered may be chosen. Bounded like a sense
 * phrase (`disambiguation-classifier.ts` caps them at 80 chars).
 */
export const ChooseSenseReqSchema = z.object({ sense: z.string().min(1).max(80) });

/**
 * Hard ceiling on tuner rounds (FR-24.9), enforced at the trust boundary.
 *
 * Declared here rather than in `src/discovery.ts` because the client needs the
 * same number to stop offering another round, and two copies would eventually
 * disagree — the disagreement being a client that offers a button the server
 * rejects. Kept out of the server module so importing it can't pull server-only
 * code into the browser bundle.
 */
export const MAX_TUNE_ROUNDS = 6;

/** Longest free-text description the discover box accepts. */
export const MAX_DISCOVER_QUERY_LENGTH = 500;

/**
 * Body of a discovery request (NEWS-125, `docs/24-topic-discovery.md`).
 *
 * The three entry paths are a discriminated union so an invalid combination —
 * a tuner round with no anchor, a section request carrying a query — is a 400
 * rather than a silently half-honoured call to the model.
 *
 * `exclude` is deliberately **not** part of this: the server fills it in from
 * the topic list so the client cannot forget to (FR-24.11).
 */
export const DiscoverReqSchema = z.object({
  scope: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('describe'), query: z.string().max(MAX_DISCOVER_QUERY_LENGTH) }),
    z.object({
      kind: z.literal('section'),
      category: z.string().min(1),
      subcategory: z.string().min(1).nullable(),
    }),
    z.object({
      kind: z.literal('tune'),
      anchor: z.string().min(1),
      direction: z.enum(['narrower', 'similar']),
      kept: z.array(z.string()).max(100).default([]),
      skipped: z.array(z.string()).max(100).default([]),
      // Each round is a billable call, so the bound is enforced server-side and
      // not merely respected by a cooperative client.
      round: z.number().int().min(1).max(MAX_TUNE_ROUNDS),
    }),
  ]),
  limit: z.number().int().min(1).max(50).optional(),
  /**
   * Suggestions the user has already been shown, for "More" (NEWS-136).
   *
   * Deliberately **additive** rather than a way to supply `exclude` wholesale:
   * the server still always adds the topic list itself, so a client that omits
   * this can fail to get fresh ideas but can never be suggested a topic the
   * user already follows. That is FR-24.11's first layer, preserved.
   */
  seen: z.array(z.string()).max(200).optional(),
});
export type DiscoverReq = z.infer<typeof DiscoverReqSchema>;

/** One suggested topic as it reaches the client. */
export const TopicSuggestionSchema = z.object({
  name: z.string(),
  reason: z.string(),
  kind: z.enum(['ongoing', 'evergreen']),
  /** Already validated against the live taxonomy server-side (FR-24.13). */
  classification: z.object({ category: z.string(), subcategory: z.string().nullable() }).nullable(),
  /** Whether this suggestion is an existing **catalog** subject (NM-155): shared, free of the custom cap. */
  catalog: z.boolean().default(false),
  /** For a catalog suggestion, how many accounts already follow it (NM-155); 0 otherwise. */
  subscribers: z.number().int().default(0),
});

export type TopicSuggestion = z.infer<typeof TopicSuggestionSchema>;

export const DiscoverRespSchema = z.object({
  suggestions: z.array(TopicSuggestionSchema),
  /** True when this answer cost nothing because it came from the cache (FR-24.15). */
  cached: z.boolean(),
});
export type DiscoverResp = z.infer<typeof DiscoverRespSchema>;

/** What discovery has spent this process lifetime (FR-24.14). */
export const DiscoverUsageRespSchema = z.object({
  calls: z.number().int(),
  recent: z.array(
    z.object({
      at: z.string(),
      scope: z.enum(['describe', 'section', 'tune']),
      provider: z.string().nullable(),
      model: z.string().nullable(),
      status: z.enum(['succeeded', 'failed']),
      returned: z.number().int(),
      cached: z.boolean(),
      error: z.string().nullable(),
    }),
  ),
});

/** Body of an item update: bookmark and/or off-topic flag; at least one. */
export const SaveItemReqSchema = z
  .object({ saved: z.boolean(), offTopic: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' });

/** Body of a key save. The value is write-only — no endpoint ever returns it. */
export const SaveKeyReqSchema = z.object({ key: z.string().min(1).max(500) });

// Response schemas (shared with the client, which validates on receipt).

/**
 * The AI backend as the environment resolved it (NM-12).
 *
 * Provider configuration is environment-only — no picker, no persisted
 * setting — so this is how the client learns which backend is in force and
 * whether it is usable. Carried on `/api/state` (polled every 4s) because it is
 * a single small object that can change only across a restart, and the client
 * shows it as a status line rather than a control.
 */
export const SourceSchema = z.object({
  /** Active provider name (`'openai'` / `'anthropic'` / …). */
  provider: z.string(),
  /** Model id the active provider will use; '' when not applicable. */
  model: z.string(),
  /** Whether that provider is usable right now (a key is present). */
  available: z.boolean(),
  /** A short human line for the status pill — see `describeSource`. */
  detail: z.string(),
  /** True when an OpenAI primary has an armed Claude Haiku failure-fallback. */
  fallback: z.boolean(),
  /**
   * Brave Search status, when the active provider discovers its sources through
   * Brave (NM-15) — **absent** on a provider that does its own web search, so its
   * presence is itself "this install searches with Brave" (NM-180). `available`
   * is whether the Brave key is present; `detail` names the gap when it is not.
   * Feeds the admin `search` health lane; optional so older/mock sources omit it.
   */
  search: z.object({ available: z.boolean(), detail: z.string() }).optional(),
});
export type Source = z.infer<typeof SourceSchema>;

/**
 * Estimated spend for a period (NEWS-79).
 *
 * `unpricedRuns` travels with the total on purpose: a bare number would read as
 * complete, and a run whose provider reported no usage (or whose model has no
 * published price) is genuinely *unknown*, not zero. The UI says so.
 */
export const StateRespSchema = z.object({
  /**
   * The server state revision this snapshot reflects (NM-231). The client echoes
   * it back as `/api/state?waitRev=<rev>` to long-poll: the server holds the
   * request until the revision moves past it. Defaults to 0 so an older fixture
   * or a `--demo` snapshot without it still parses (the client then long-polls
   * from 0, which just answers immediately the first time).
   */
  rev: z.number().int().default(0),
  topics: z.array(TopicSchema),
  /**
   * Newest item ids across all topics, newest first (NEWS-75). The
   * notification detector reads this; the feed itself comes from `/api/items`
   * (NEWS-76), so `/api/state` no longer carries the full item list.
   */
  latestItemIds: z.array(z.string()).default([]),
  /** Off-topic story count per topic, for the "Review Flagged (N)" badge (NEWS-76). */
  flaggedByTopic: z.record(z.string(), z.number().int()).default({}),
  /** Stories found today per topic, for the sidebar count badge (NEWS-242). Absent = none. */
  todayByTopic: z.record(z.string(), z.number().int()).default({}),
  /** Newest story's `foundAt` per topic, for the most-recent sort (NEWS-241). */
  newestItemAtByTopic: z.record(z.string(), z.string()).default({}),
  settings: SettingsSchema,
  runs: z.array(CheckRunSchema),
  checking: z.array(z.string()),
  /** App version, for diagnostics bundles (NEWS-88). '' if it can't be read. */
  appVersion: z.string().default(''),
  /**
   * When scheduled checking last became possible — the later of server start
   * and the last sweep that was turned away (NEWS-247).
   *
   * The "falling behind" banner measures lateness from here rather than from
   * `lastCheckedAt` alone, because wall-clock cannot tell *we cannot keep up*
   * from *we were not permitted to try*. Defaults to epoch 0 so an old client
   * or a fixture without it behaves exactly as before.
   */
  checksPossibleSince: z.string().default('1970-01-01T00:00:00.000Z'),
  /**
   * An unread notice that a database was set aside as unreadable (NEWS-340).
   * `null` in the overwhelmingly normal case. Defaulted so an older server —
   * or a response cached across an upgrade — parses rather than blanking the
   * whole state on a field the client is only going to render a banner from.
   */
  quarantine: QuarantineSchema.nullable().default(null),
  /**
   * A stable id for this database (NEWS-423), so the client can tell a dismissed
   * setup guide from one dismissed against a database that no longer exists.
   *
   * Defaulted to `''` because it is read by a poll: an older server, or a
   * response cached across an upgrade, must not blank the whole state. An empty
   * id reads as "cannot tell", and the client treats that as *seen* rather than
   * unseen — the wrong guess in that direction is a missing prompt, not a wizard
   * over an established user's feed.
   */
  installId: z.string().default(''),
  /**
   * The AI backend the environment resolved (NM-12), so the client can show
   * which provider is in force and whether it is usable. Defaulted so an older
   * client — or a response cached across an upgrade — parses rather than
   * blanking the whole state on a field it only renders a status line from.
   */
  source: SourceSchema.default({
    provider: '',
    model: '',
    available: false,
    detail: 'No AI provider configured — set OPENAI_API_KEY or ANTHROPIC_API_KEY',
    fallback: false,
  }),
  /**
   * Maintenance mode's **public** face (NM-75): whether an operator has posted a
   * maintenance banner and its message. Only these two fields cross to the
   * reader — the internal `pauseChecks`/`updatedBy` are console-only. Defaulted
   * (off, empty) so an older client or a cached response parses rather than
   * blanking the state on a field it only renders a banner from.
   */
  maintenance: z.object({ enabled: z.boolean(), message: z.string() }).default({ enabled: false, message: '' }),
  /**
   * The reader's plan and how much of it they're using (NM-135), so the client can
   * show "X of N topics" and make the plan-limit rejection legible. `null` on the
   * non-enforcing default-account path (no session) — there is no plan to meter
   * there. `catalogCap` is `null` when a tier's catalog subscriptions are
   * unlimited. Defaulted to null so an older client or a cached response parses.
   */
  plan: z
    .object({
      id: z.string(),
      label: z.string(),
      customCount: z.number().int(),
      customCap: z.number().int(),
      catalogCount: z.number().int(),
      catalogCap: z.number().int().nullable(),
    })
    .nullable()
    .default(null),
  /**
   * Self-serve upgrade (NM-225/226). `enabled` is the mock checkout being available
   * (dev/beta only — a real production stack has no mock billing); when false the
   * client shows no upgrade affordance. `plans` is the full tier table (label,
   * monthly price, caps, and since NM-271 the plan's check cadence) so the
   * checkout can list the tiers above the current one. Defaulted so an older or
   * cached response parses; `checkIntervalMs` optional for the same reason.
   */
  billing: z
    .object({
      enabled: z.boolean(),
      plans: z.array(
        z.object({
          id: z.string(),
          label: z.string(),
          priceUsd: z.number(),
          customCap: z.number().int(),
          catalogCap: z.number().int().nullable(),
          checkIntervalMs: z.number().int().optional(),
        }),
      ),
    })
    .default({ enabled: false, plans: [] }),
  /**
   * The RTDB change-signal channel (NM-250), or null on the long-poll deploy.
   * `url` is the RTDB base; `channel` is this account's own unguessable key. The
   * reader waits on `url/signals/channel.json` (EventSource) instead of holding
   * the long-poll open, and only refetches when the marker moves.
   */
  signal: z
    .object({
      url: z.string(),
      channel: z.string(),
    })
    .nullable()
    .default(null),
  /**
   * The Web Push application-server (VAPID) public key (NM-251), or null when
   * push is not configured. The client subscribes with it (behind the notify
   * setting + a permission grant) and posts the subscription to
   * `/api/push/subscribe`.
   */
  pushPublicKey: z.string().nullable().default(null),
});
export type StateResp = z.infer<typeof StateRespSchema>;

/** Request body for `POST /api/push/subscribe` (NM-251) — the browser's `PushSubscription.toJSON()`. */
export const PushSubscribeReqSchema = z.object({
  endpoint: z.url(),
  keys: z
    .object({
      p256dh: z.string().optional(),
      auth: z.string().optional(),
    })
    .optional(),
});
export type PushSubscribeReq = z.infer<typeof PushSubscribeReqSchema>;

/** Request body for `POST /api/push/unsubscribe` (NM-251). */
export const PushUnsubscribeReqSchema = z.object({ endpoint: z.string() });

/** Request body for the mock upgrade checkout (NM-225/226). */
export const UpgradeReqSchema = z.object({
  plan: z.string(),
  card: z.string(),
});
export type UpgradeReq = z.infer<typeof UpgradeReqSchema>;

/** One local-calendar day in a topic/category pulse (NEWS-453). */
export const PulsePointSchema = z.object({
  date: z.string(),
  stories: z.number().int().nonnegative(),
  updates: z.number().int().nonnegative(),
});

/** The small, always-cheap shape used by topic-row sparklines (NEWS-453). */
export const TopicSparklineRespSchema = z.object({
  byTopic: z.record(z.string(), z.array(z.number().int().nonnegative()).length(7)),
});
export type TopicSparklineResp = z.infer<typeof TopicSparklineRespSchema>;

const PulseSourceSchema = z.object({
  label: z.string(),
  count: z.number().int().positive(),
  share: z.number().min(0).max(1),
});

const PulseThreadSchema = z.object({
  id: z.string(),
  title: z.string(),
  updates: z.number().int().nonnegative(),
  startedAt: z.string(),
  latestAt: z.string(),
});

/** Deterministic archive analysis for one topic or taxonomy selection (NEWS-453). */
export const PulseRespSchema = z.object({
  scope: z.object({
    kind: z.enum(['topic', 'category']),
    id: z.string(),
    subcategory: z.string().nullable(),
    label: z.string(),
  }),
  days: z.union([z.literal(7), z.literal(30), z.literal(90)]),
  storyCount: z.number().int().nonnegative(),
  previousStoryCount: z.number().int().nonnegative(),
  trendPercent: z.number().nullable(),
  activeThreads: z.number().int().nonnegative(),
  distinctOutlets: z.number().int().nonnegative(),
  sourcedStories: z.number().int().nonnegative(),
  topSourceShare: z.number().min(0).max(1).nullable(),
  smallSample: z.boolean(),
  series: z.array(PulsePointSchema),
  cadence: z.object({
    averageDays: z.number().nullable(),
    longestQuietDays: z.number().int().nonnegative(),
    mostActiveDate: z.string().nullable(),
    mostActiveCount: z.number().int().nonnegative(),
  }),
  sources: z.array(PulseSourceSchema),
  otherSourceCount: z.number().int().nonnegative(),
  threads: z.array(PulseThreadSchema),
});
export type PulseResp = z.infer<typeof PulseRespSchema>;

/**
 * Where one story sits in its thread (NEWS-282), for the collapsed card's badge
 * (NEWS-283).
 *
 * The **shape** of a thread, not its contents: three numbers per story, which is
 * what a badge can say without the timeline being fetched. The stories
 * themselves come from `GET /api/items/:id/thread`, on expand — folding them
 * into the feed page would multiply a size-sensitive payload by the average
 * thread length for a pane nobody has opened (`docs/17-server-pagination.md`).
 */
export const ThreadSummarySchema = z.object({
  /** 1-based position in the thread, chronologically. */
  position: z.number().int(),
  /** How many stories the thread holds. Always ≥ 2 — see below. */
  size: z.number().int(),
  /** `foundAt` of the thread's first story: when the subject first appeared. */
  startedAt: z.string(),
});
export type ThreadSummary = z.infer<typeof ThreadSummarySchema>;

/** A feed page (server-side pagination, NEWS-74). */
export const ItemCursorSchema = z.object({ foundAt: z.string(), id: z.string() });
export const ItemsRespSchema = z.object({
  items: z.array(NewsItemSchema),
  nextCursor: ItemCursorSchema.nullable(),
  total: z.number().int(),
  /**
   * Thread shape per story id (NEWS-282), for the stories on **this page**.
   *
   * **Only stories in a thread of two or more get an entry**, which is what
   * keeps this from growing the payload: a thread of one is the ordinary case
   * (FR-29.6), so on a feed with no threading yet this map is empty and the
   * response is byte-for-byte what it was before. It is also what tells the
   * client there is nothing to fetch when a card is expanded.
   */
  threads: z.record(z.string(), ThreadSummarySchema).default({}),
});
export type ItemsResp = z.infer<typeof ItemsRespSchema>;

/** One story's whole thread, oldest first — `GET /api/items/:id/thread` (NEWS-282). */
export const ThreadRespSchema = z.object({ items: z.array(NewsItemSchema) });
export type ThreadResp = z.infer<typeof ThreadRespSchema>;

/**
 * What `POST /api/items/clear` did (NEWS-255, extended in NEWS-271).
 *
 * `cancelledChecks` is defaulted rather than required so an older client parsing
 * a newer server — or the reverse — does not fail on a field it has no use for.
 */
export const ClearItemsRespSchema = z.object({
  cleared: z.number().int(),
  cancelledChecks: z.number().int().default(0),
});

// The set-aside/recover request+response schemas (FR-33) went with the in-app
// Recovery UI in NM-171 — recovering a quarantined database is a terminal/operator
// step now (see store.ts's startup log and FR-4.9). The FR-4.17 quarantine notice
// keeps its own `QuarantineSchema`.

/** What a bulk topic delete did (FR-31.1, NEWS-328). */
export const ClearTopicsRespSchema = z.object({
  deleted: z.number().int(),
  cancelledChecks: z.number().int().default(0),
});

/**
 * The signed-in account as `/api/session` and `/api/register` report it (NM-20,
 * phase 3). A projection of the `Account` row down to what the client gate needs
 * — never the identity issuer/subject, which stay server-side.
 */
export const SessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: z.enum(['member', 'admin']),
  status: z.enum(['active', 'suspended', 'disabled']),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

/**
 * `GET /api/session`, with **three** shapes the client gate switches on (NM-20,
 * phase 3):
 *  - `{ user: {...} }` — a verified identity *with* a registered account.
 *  - `{ user: null, pendingRegistration: {...} }` — a verified identity with no
 *    account yet (the `new-user` persona): the registration flow.
 *  - `{ user: null }` — no identity at all: signed out.
 *
 * `pendingRegistration` present is exactly what separates "unregistered" from
 * "signed out", so the field is optional rather than nullable.
 */
export const SessionRespSchema = z.object({
  user: SessionUserSchema.nullable(),
  pendingRegistration: z.object({ email: z.string().nullable(), displayName: z.string() }).optional(),
  /** Whether the sign-in gate should offer "Sign in with Apple" (NM-252) — true only when Apple is configured on the deploy. Absent reads as false. */
  appleSignIn: z.boolean().optional(),
});
export type SessionResp = z.infer<typeof SessionRespSchema>;

/** `POST /api/register`: the fresh (or already-registered) account, after re-issuing the session. */
export const RegisterRespSchema = z.object({
  ok: z.literal(true),
  user: SessionUserSchema,
});
export type RegisterResp = z.infer<typeof RegisterRespSchema>;
